import React from 'react';
import { clsx } from 'clsx';
import {
  ArrowTopRightOnSquareIcon,
  BellIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  BanknotesIcon,
  DocumentCheckIcon,
  XCircleIcon,
  ChartBarIcon,
  XMarkIcon,
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  PercentBadgeIcon,
} from '@heroicons/react/24/outline';
import { TradeItem, EffectiveTradeStatus } from '@/types';
import { getEffectiveTradeStatus, getEffectiveTradeStatusLabel } from '@/utils/trades/effective-status';
import { retryBlockedReason } from '@/utils/trades/bo-posting-copy';
import {
  collateralLegName,
  getValidActions,
  isTerminalStatus,
  SET_CLOSE_DATE_TOOLTIP,
  TERMINATE_TOOLTIP,
  CANCEL_REJECTED_TOOLTIP,
  type ActionId,
} from '../hooks/trade-actions';
import { formatCurrencyExact } from '@/utils/trades/mtm-calculations';
import { getSettlementDisplay } from '@/utils/trades/settlement-ccy';
import { Z_INDEX } from '@/utils/z-index';
import {
  useElementHeight,
  useShellInsets,
  SHELL_GUTTER_PX,
  SCROLL_TOP_BUTTON_LANE_PX,
} from '@/hooks/common';
import { FAB_BOTTOM_OFFSET_PX } from './fab-geometry';

interface FloatingActionBarProps {
  /** The single selected position the panel acts on. */
  selectedTrade: TradeItem | null;
  /** Selection size — the panel is strictly single-position (renders only at 1). */
  selectedCount: number;
  onClearSelection: () => void;
  /** Open the single-trade page (/trades/{id}). */
  onOpen: (trade: TradeItem) => void;
  /** Dispatch a lifecycle action (opens its confirm dialog in TradesPage). */
  onAction: (actionId: ActionId, trade: TradeItem) => void;
  /** Navigate to the Mark-to-Market view for this trade's counterparty. */
  onMtm: (trade: TradeItem) => void;
  /** Open the BO-error detail modal (LR-408 — rejected rows). */
  onShowBoError: (trade: TradeItem) => void;
  /** LR-257: whether the user may mutate trades (`create_trades`). When false,
   *  lifecycle + Terminate actions are hidden; Open / Mark-to-Market remain. */
  canTrade: boolean;
  /** LR-600: rendered height (px), reported on mount/resize and 0 on unmount, so
   *  the table can size its scroll clearance to a bar that wrapped to two rows. */
  onHeightChange?: (px: number) => void;
  /** LR-600: whether the scroll-to-top button is on screen — the bar then keeps its
   *  corner lane clear instead of clipping it. */
  scrollTopVisible?: boolean;
}

// LR-497: bright on-gradient palette for the FAB info strip — mirrors the hue
// family of the pale TRADE_STATUS_COLORS badge (yellow=pending, amber=partially
// settled, purple=pending partial) at higher saturation so it reads on the blue
// header. Keyed by the EFFECTIVE status so the chip's colour + label match the
// badge the trader clicked, derived buckets included. In lifecycle order.
const statusColors: Record<EffectiveTradeStatus, string> = {
  pending: 'bg-yellow-400 text-yellow-950',
  pending_open_settlement: 'bg-cyan-400 text-cyan-950',
  partially_settled: 'bg-amber-400 text-amber-950',
  active: 'bg-emerald-400 text-emerald-950',
  pending_partial_settlement: 'bg-purple-400 text-purple-950',
  matured: 'bg-orange-400 text-orange-950',
  closed: 'bg-slate-300 text-slate-800',
  cancelled: 'bg-rose-400 text-rose-950',
  rejected: 'bg-red-400 text-red-950',
};

const btnBase =
  'inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg border transition-colors cursor-pointer whitespace-nowrap';
const btnWhite =
  'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400';
const btnBlue =
  'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 hover:border-blue-700';
const btnRed =
  'bg-white text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400';

// Non-destructive lifecycle actions in display order. Terminate is rendered
// separately (destructive, last). Each entry carries its canonical label + icon
// (kept aligned with the prototype trade-detail action bar). Only entries whose
// id is in getValidActions(trade) render — contextual, never pre-baked.
const LIFECYCLE_ACTIONS: ReadonlyArray<{
  id: Exclude<ActionId, 'terminate'>;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  /** Optional hover hint; LR-576 (feedback п.70) uses it to disambiguate the
      close-date scheduler from Terminate. */
  tooltip?: string;
}> = [
  // LR-484: amend "the numbers" on a pending trade — opens the reused booking
  // form (gated pending-only by getValidActions, so it surfaces only where valid).
  { id: 'amend', label: 'Amend', Icon: PencilSquareIcon },
  { id: 'give_notice', label: 'Give Notice', Icon: BellIcon },
  // LR-576 (feedback п.70): the action stamps the CLOSE settlement date, so it
  // is named after the LR-406 blotter column ("Set Close Date") — the previous
  // open-side-sounding label was indistinguishable from Terminate for the customer.
  {
    id: 'set_settlement_date',
    label: 'Set Close Date',
    Icon: CalendarDaysIcon,
    tooltip: SET_CLOSE_DATE_TOOLTIP,
  },
  // LR-539: single re-rate from the blotter (opens the LR-528 ReRateModal on a
  // fetched PositionDetail). Gated active/matured by getValidActions.
  { id: 're_rate', label: 'Re-rate', Icon: PercentBadgeIcon },
  { id: 'settle', label: 'Settle', Icon: CheckCircleIcon },
  { id: 'settle_cash', label: 'Settle Cash', Icon: BanknotesIcon },
  { id: 'settle_securities', label: 'Settle Securities', Icon: DocumentCheckIcon },
  // LR-388: label is resolved per trade side at render (lend → Recall, borrow → Return).
  { id: 'partial_recall', label: 'Partial Recall', Icon: ArrowUturnLeftIcon },
  { id: 'retry', label: 'Retry', Icon: ArrowPathIcon },
];

export const FloatingActionBar: React.FC<FloatingActionBarProps> = ({
  selectedTrade,
  selectedCount,
  onClearSelection,
  onOpen,
  onAction,
  onMtm,
  onShowBoError,
  canTrade,
  onHeightChange,
  scrollTopVisible = false,
}) => {
  // Hooks stay above the early return (rules of hooks) — the bar is conditional.
  // LR-600: the gutter grows by the scroll-to-top button's lane while that button is
  // up, so the bar shrinks out of its corner instead of overlapping it (QA). Added to
  // BOTH sides on purpose — equal gutters cancel out, so the bar stays centred on the
  // table; a right-only inset would slide it off centre by half the lane.
  const insets = useShellInsets(
    SHELL_GUTTER_PX + (scrollTopVisible ? SCROLL_TOP_BUTTON_LANE_PX : 0),
  );
  const measureRef = useElementHeight(onHeightChange);

  // Strictly single-position. LR-539 brought multi-select back, so >1 IS now
  // reachable — but at >1 TradesPage renders BulkActionBar instead and passes
  // selectedTrade={null} (it resolves a trade only at count===1), so this FAB
  // hides here and the two bars never both show.
  if (selectedCount !== 1 || !selectedTrade) return null;

  const valid = getValidActions(selectedTrade);

  return (
    <div
      ref={measureRef}
      data-tour="trades-floating-action-bar"
      // LR-600: left/right are the app-shell insets, not 0 — a fixed box ignores
      // <main>'s pl-*/pr-*, so `left-0 right-0` centred the bar on the VIEWPORT
      // and slid it under the z-9998 filter rail (108px off at 1384px with the
      // rail open). With w-fit, the insets both centre the bar in the content
      // band and shrink-to-fit it to that band's width — no max-width needed
      // (a percentage max-width would resolve against the viewport instead).
      style={{
        zIndex: Z_INDEX.FLOATING_ACTION_BAR,
        bottom: FAB_BOTTOM_OFFSET_PX,
        left: insets.left,
        right: insets.right,
      }}
      className={clsx(
        'fixed mx-auto w-fit',
        'flex flex-col overflow-hidden rounded-xl',
        'shadow-2xl shadow-blue-900/30',
        // In step with the rails' own transition-all duration-300.
        'motion-safe:transition-[left,right] motion-safe:duration-300 motion-safe:ease-in-out',
        'motion-safe:animate-[slideInUp_200ms_ease-out]'
      )}
    >
      {/* Row 1: Blue accent info strip for the selected position. Leads with the
          trade id (the legacy "N selected" badge was a leftover from multi-select
          — the panel is single-position now). */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-5 py-3 flex flex-wrap items-center gap-4">
        <span className="text-base font-mono font-bold text-white tabular-nums">
          #{selectedTrade.id}
        </span>
        <span className="text-base font-bold text-white">{selectedTrade.symbol_id}</span>
        <span
          className={clsx(
            'px-2.5 py-1 rounded text-sm font-semibold',
            statusColors[getEffectiveTradeStatus(selectedTrade)]
          )}
        >
          {getEffectiveTradeStatusLabel(selectedTrade)}
        </span>
        {/* ml-auto pushes Value to the right edge of the info strip,
            separating identity (id / symbol / status) from the figure. */}
        {/* LR-474: settlement-ccy figure with its real ISO symbol — the old
            hardcoded '$' mislabelled the native sum on any non-USD trade. */}
        <span className="ml-auto text-base text-white/70">
          Value:{' '}
          <span className="text-white font-mono font-bold tabular-nums">
            {(() => {
              const d = getSettlementDisplay(selectedTrade, 'contract_value');
              return d === null ? '—' : formatCurrencyExact(d.value, d.ccy);
            })()}
          </span>
        </span>
      </div>

      {/* Row 2: contextual action buttons — Open, valid lifecycle, MTM, Terminate.
          LR-600: flex-wrap is what makes the reflow work — without it the row's
          min-content width is the sum of all (whitespace-nowrap) buttons, so the
          bar could not shrink into the content band and overflow-hidden clipped it. */}
      <div className="bg-white px-4 py-2.5 flex flex-wrap items-center justify-center gap-2">
        {/* Open — always available (navigation), primary blue, first. */}
        <button
          type="button"
          className={clsx(btnBase, btnBlue)}
          onClick={() => onOpen(selectedTrade)}
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          Open
        </button>

        {/* Valid non-destructive lifecycle actions, contextual. */}
        {canTrade && LIFECYCLE_ACTIONS.filter(({ id }) => valid.has(id)).map(({ id, label, Icon, tooltip }) => {
          // LR-388: the partial-recall button reads "Partial Return" on a borrow (we return
          // borrowed shares) and "Partial Recall" on a lend (we recall lent shares).
          const displayLabel =
            id === 'partial_recall' && selectedTrade.trade_type === 'borrow'
              ? 'Partial Return'
              : id === 'settle_cash'
                ? `Settle ${collateralLegName(selectedTrade.collateral_type)}`
                : label;
          // LR-714: Retry is refused while BackOffice may already hold the
          // posting — the same fact the card's banner states as "Do NOT retry it
          // yet". Offering the button anyway made the two disagree, and the
          // button is the more believable of the two. Disabled rather than
          // hidden, and disabled the LR-544 way (`aria-disabled`, not the
          // attribute, so it keeps its place in the tab order and announces the
          // state): a Retry that silently vanished would read as a bug on the
          // one screen where the operator is already unsure what happened, and
          // the tooltip is where the remedy gets named.
          const blockedReason = id === 'retry'
            ? retryBlockedReason(selectedTrade.bo_posting)
            : null;
          return (
            <button
              key={id}
              type="button"
              title={blockedReason ?? tooltip}
              aria-disabled={blockedReason ? true : undefined}
              className={clsx(
                btnBase,
                btnWhite,
                blockedReason && 'opacity-50 cursor-not-allowed hover:bg-white',
              )}
              onClick={blockedReason ? undefined : () => onAction(id, selectedTrade)}
            >
              <Icon className="h-4 w-4" />
              {displayLabel}
            </button>
          );
        })}

        {/* Mark-to-Market — navigation to the CP's LIVE MTM view (not a lifecycle
            action). Hidden for terminal positions: a closed trade has no live mark —
            a future "View MTM History" action will serve those positions instead. */}
        {!isTerminalStatus(selectedTrade.status) && selectedTrade.status !== 'rejected' && (
          <button
            type="button"
            className={clsx(btnBase, btnWhite)}
            onClick={() => onMtm(selectedTrade)}
          >
            <ChartBarIcon className="h-4 w-4" />
            Mark-to-Market
          </button>
        )}

        {/* View BO Error — LR-408: rejected rows with a recorded BO failure.
            LR-544: also for a pending row still retrying (bo_retry_count > 0),
            so the operator can inspect the failure without opening the trade.
            LR-712: offered when there is a posting to explain — `bo_posting` first,
            and the legacy string only for a trade with no posting record at all
            (rejected before LR-709 shipped the attempt log). Gating on
            `last_bo_error` alone used to hide the button on exactly the trades
            that needed it most: a timed-out posting whose column had been cleared
            by a later success left the operator no way in from the blotter. */}
        {(selectedTrade.status === 'rejected' ||
          (selectedTrade.status === 'pending' && (selectedTrade.bo_retry_count ?? 0) > 0)) &&
          (selectedTrade.bo_posting || selectedTrade.last_bo_error) && (
          <button
            type="button"
            className={clsx(btnBase, btnWhite)}
            onClick={() => onShowBoError(selectedTrade)}
          >
            <ExclamationTriangleIcon className="h-4 w-4" />
            View BO Error
          </button>
        )}

        {/* Terminate (Cancel for a rejected, never-opened position) — destructive, last. */}
        {canTrade && valid.has('terminate') && (
          <button
            type="button"
            // LR-576 (feedback п.70): disambiguate from Set Close Date on hover.
            title={selectedTrade.status === 'rejected' ? CANCEL_REJECTED_TOOLTIP : TERMINATE_TOOLTIP}
            className={clsx(btnBase, btnRed)}
            onClick={() => onAction('terminate', selectedTrade)}
          >
            <XCircleIcon className="h-4 w-4" />
            {selectedTrade.status === 'rejected' ? 'Cancel' : 'Terminate'}
          </button>
        )}

        <div className="w-px h-6 bg-gray-200 mx-1" />
        <button
          type="button"
          className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          onClick={onClearSelection}
          aria-label="Clear selection"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};
